import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router";
import { Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import { useAtomValue } from "jotai";
import { TooltipProvider } from "./components/ui/Tooltip";
import { AppShell } from "./components/layout/AppShell";
import { AdminShell } from "./components/layout/AdminShell";
import { DealerShell } from "./components/layout/DealerShell";
import { RouteErrorElement } from "./components/routing/RouteErrorElement";
import { ImpersonationBlocked } from "./features/admin/components/ImpersonationBlocked";
import LoginPage from "./pages/LoginPage";
import { useAuthStore } from "./store/auth";
import {
  impersonationContextAtom,
  isImpersonatingAtom,
} from "./store/impersonation";

// Eagerly loaded pages. The Dashboard body is eager-loaded (via DashboardTab,
// hosted inside AnalyticsPage) so the dispatcher's default tab paints fast.
import AnalyticsPage from "./pages/AnalyticsPage";
import NotFoundPage from "./pages/NotFoundPage";

// Lazy-loaded public pages
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const PublicTrackingPage = lazy(() => import("./pages/PublicTrackingPage"));
const SsoCallbackPage = lazy(() => import("./pages/SsoCallbackPage"));

// Lazy-loaded feature pages
const RoutesSectionPage = lazy(() => import("./pages/RoutesSectionPage"));
const RouteDetailPage = lazy(() => import("./pages/RouteDetailPage"));
const WorkingCalendarPage = lazy(() => import("./pages/WorkingCalendarPage"));
const ScheduleRunBoardPage = lazy(() => import("./pages/ScheduleRunBoardPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"));
const WarehousesPage = lazy(() => import("./pages/WarehousesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const LiveTrackingPage = lazy(() => import("./pages/LiveTrackingPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const DriverMobilePage = lazy(() => import("./pages/DriverMobilePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AdminTenantsPage = lazy(() => import("./pages/AdminTenantsPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const AdminTenantDetailPage = lazy(
  () => import("./pages/AdminTenantDetailPage"),
);
const AdminSystemSettingsPage = lazy(
  () => import("./pages/AdminSystemSettingsPage"),
);
const AdminActivityLogPage = lazy(() => import("./pages/AdminActivityLogPage"));
const AdminAlertsPage = lazy(() => import("./pages/AdminAlertsPage"));
const AdminDealersPage = lazy(() => import("./pages/AdminDealersPage"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsersPage"));
const AdminDealerDetailPage = lazy(
  () => import("./pages/AdminDealerDetailPage"),
);
const AdminDealerDomainsPage = lazy(
  () => import("./pages/AdminDealerDomainsPage"),
);
const AdminSystemStatusPage = lazy(
  () => import("./pages/AdminSystemStatusPage"),
);
const AdminBillingPage = lazy(() => import("./pages/AdminBillingPage"));
const AdminPlansPage = lazy(() => import("./pages/AdminPlansPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const AdminIntegrationsPage = lazy(
  () => import("./pages/AdminIntegrationsPage"),
);
const AdminBroadcastPage = lazy(() => import("./pages/AdminBroadcastPage"));
const DealerBrandingPage = lazy(() => import("./pages/DealerBrandingPage"));
const DealerDomainsPage = lazy(() => import("./pages/DealerDomainsPage"));

/**
 * AdminRoute — guards /admin/* routes.
 * - No token → redirect to /login
 * - Token present but user not yet loaded → wait (null) to avoid premature redirect
 * - User loaded but not superadmin → redirect to /
 * - Superadmin → render children via <Outlet />
 */
/** Role-appropriate home so a misrouted user lands in their own panel, not the
 * dispatcher app (e.g. a dealer hitting /admin/* goes to /dealer, not /). */
function homePathForRole(role: string | undefined): string {
  if (role === "superadmin") return "/admin";
  if (role === "dealer") return "/dealer";
  return "/";
}

function AdminRoute() {
  const token = localStorage.getItem("auth_token");
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAtomValue(isImpersonatingAtom);

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  // User is still hydrating from storage — wait to avoid flash-redirect.
  if (!user) {
    return null;
  }
  // During an active impersonation the live identity is the tenant
  // company_admin, so the role check below would silently bounce the user to
  // "/". Instead show an explicit "you are impersonating <tenant>" state with a
  // clear exit action (TASK-3677) rather than a confusing silent redirect.
  if (isImpersonating) {
    return <ImpersonationBlocked />;
  }
  if (user.role !== "superadmin") {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }
  return <Outlet />;
}

/**
 * DealerRoute — guards /dealer/* routes.
 * - No token → redirect to /login
 * - Token present but user not yet loaded → wait (null) to avoid premature redirect
 * - User loaded but not a dealer → redirect to /
 * - Dealer → render children via <Outlet />
 */
function DealerRoute() {
  const token = localStorage.getItem("auth_token");
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAtomValue(isImpersonatingAtom);
  const impersonationContext = useAtomValue(impersonationContextAtom);

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  // User is still hydrating from storage — wait to avoid flash-redirect.
  if (!user) {
    return null;
  }
  // Sprint 139: a superadmin impersonating a DEALER legitimately operates as
  // that dealer inside the /dealer shell — let it through (banner + exit are
  // rendered by DealerShell). Any OTHER active impersonation reaching a
  // /dealer route is a tenant impersonation (the live identity is a tenant
  // company_admin); show the explicit impersonating state with an exit action
  // instead of silently redirecting (TASK-3677).
  if (isImpersonating && impersonationContext?.targetKind !== "dealer") {
    return <ImpersonationBlocked />;
  }
  if (user.role !== "dealer") {
    return <Navigate to={homePathForRole(user.role)} replace />;
  }
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    // BUG-745: one root error element so NO route can fall through to React
    // Router's developer error page. Each shell additionally nests its
    // children under a pathless errorElement route so a page error keeps the
    // shell (sidebar, header) around the error screen.
    errorElement: <RouteErrorElement fullScreen />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/forgot-password",
        element: (
          <Suspense fallback={null}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "/reset-password",
        element: (
          <Suspense fallback={null}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "/set-password",
        element: (
          <Suspense fallback={null}>
            <SetPasswordPage />
          </Suspense>
        ),
      },
      {
        // PUBLIC SSO callback (Sprint 152, TASK-4333). Standalone top-level route
        // with NO auth guard: the IdP redirects the browser here with a single-use
        // `?code` (or `?error`). The page exchanges the code for tokens, then
        // persists the session (or enters the MFA branch) and routes by role.
        path: "/auth/sso/callback",
        element: (
          <Suspense fallback={null}>
            <SsoCallbackPage />
          </Suspense>
        ),
      },
      {
        path: "/driver-mobile",
        element: (
          <Suspense fallback={null}>
            <DriverMobilePage />
          </Suspense>
        ),
      },
      {
        // Public, UNAUTHENTICATED recipient tracking page (TASK-3730). Standalone
        // top-level route — intentionally OUTSIDE AppShell/AdminShell/DealerShell
        // and with NO auth guard: recipients open it from the SMS/email link.
        path: "/track/:token",
        element: (
          <Suspense fallback={null}>
            <PublicTrackingPage />
          </Suspense>
        ),
      },
      {
        // AdminRoute guard + AdminShell layout for all /admin/* routes.
        element: <AdminRoute />,
        children: [
          {
            element: <AdminShell />,
            children: [
              {
                errorElement: <RouteErrorElement />,
                children: [
                  {
                    path: "/admin/dashboard",
                    element: (
                      <Suspense fallback={null}>
                        <AdminDashboardPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/admin/tenants",
                    element: (
                      <Suspense fallback={null}>
                        <AdminTenantsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/admin/tenants/:id",
                    element: (
                      <Suspense fallback={null}>
                        <AdminTenantDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only Dealers management (TASK-3646). Intentionally NOT
                    // registered under the /dealer shell — dealers cannot manage dealers.
                    path: "/admin/dealers",
                    element: (
                      <Suspense fallback={null}>
                        <AdminDealersPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only dealer detail (TASK-3672). Not in the /dealer
                    // shell — dealers cannot view other dealers.
                    path: "/admin/dealers/:id",
                    element: (
                      <Suspense fallback={null}>
                        <AdminDealerDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only cross-dealer cert-monitoring dashboard
                    // (White-Label Phase E, TASK-3804). Lists every dealer's custom
                    // domain. Not registered under the /dealer shell.
                    path: "/admin/dealer-domains",
                    element: (
                      <Suspense fallback={null}>
                        <AdminDealerDomainsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only live platform health dashboard (Sprint 143,
                    // TASK-4242): dependency checks, worker liveness, queue depth,
                    // failed tasks (24h), error rate. Not registered under /dealer.
                    path: "/admin/system-status",
                    element: (
                      <Suspense fallback={null}>
                        <AdminSystemStatusPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only global cross-tenant user management (TASK-3685).
                    // Not registered under the /dealer shell.
                    path: "/admin/users",
                    element: (
                      <Suspense fallback={null}>
                        <AdminUsersPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/admin/billing",
                    element: (
                      <Suspense fallback={null}>
                        <AdminBillingPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only global plan catalog (Sprint 147, TASK-4283).
                    // Intentionally NOT registered under the /dealer shell — dealers
                    // 403 on the catalog CRUD endpoints.
                    path: "/admin/plans",
                    element: (
                      <Suspense fallback={null}>
                        <AdminPlansPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/admin/activity-log",
                    element: (
                      <Suspense fallback={null}>
                        <AdminActivityLogPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Alerts inbox (TASK-3676) — reached from the bell's "View all".
                    path: "/admin/alerts",
                    element: (
                      <Suspense fallback={null}>
                        <AdminAlertsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Old separate-log routes redirect to the merged Activity Log
                    // (TASK-514) so existing links/bookmarks keep working.
                    path: "/admin/impersonations",
                    element: <Navigate to="/admin/activity-log" replace />,
                  },
                  {
                    path: "/admin/audit-log",
                    element: <Navigate to="/admin/activity-log" replace />,
                  },
                  {
                    // Cross-tenant API keys & webhooks (TASK-3725). Same page used under
                    // the /dealer shell; data scoped server-side by role.
                    path: "/admin/integration",
                    element: (
                      <Suspense fallback={null}>
                        <AdminIntegrationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin-only broadcast compose screen (TASK-3931). NOT
                    // registered under the /dealer shell — dealers cannot author
                    // broadcasts (matches the SUPERADMIN-ONLY backend permission).
                    path: "/admin/broadcast",
                    element: (
                      <Suspense fallback={null}>
                        <AdminBroadcastPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Sectioned settings sub-nav (TASK-4223), URL-synced via `:section`.
                    // Bare `/admin/settings` redirects to the default General section.
                    path: "/admin/settings",
                    element: <Navigate to="/admin/settings/general" replace />,
                  },
                  {
                    path: "/admin/settings/:section",
                    element: (
                      <Suspense fallback={null}>
                        <AdminSystemSettingsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Superadmin self-service profile & security (Sprint 144). Renders
                    // the same ProfilePage used by the dispatcher shell so a superadmin
                    // can change their password and enrol in MFA — the AppShell guard
                    // redirects superadmin away from the dispatcher /profile, so this
                    // in-shell route is where the "My Profile" menu item resolves.
                    path: "/admin/profile",
                    element: (
                      <Suspense fallback={null}>
                        <ProfilePage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // DealerRoute guard + DealerShell layout for all /dealer/* routes. The
        // pages are the same components used by /admin; they hit the dealer-aware
        // admin endpoints, scoped server-side, and render their internal links
        // under /dealer via the AdminBasePathContext provided by DealerShell.
        element: <DealerRoute />,
        children: [
          {
            element: <DealerShell />,
            children: [
              {
                errorElement: <RouteErrorElement />,
                children: [
                  {
                    path: "/dealer",
                    element: <Navigate to="/dealer/dashboard" replace />,
                  },
                  {
                    path: "/dealer/dashboard",
                    element: (
                      <Suspense fallback={null}>
                        <AdminDashboardPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/dealer/tenants",
                    element: (
                      <Suspense fallback={null}>
                        <AdminTenantsPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/dealer/tenants/:id",
                    element: (
                      <Suspense fallback={null}>
                        <AdminTenantDetailPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Users management under the dealer shell (TASK-4225). Same page as
                    // /admin/users; the backend scopes the list to the dealer's own
                    // tenants. Exists so command-palette user results deep-link here
                    // instead of a dead /dealer/users → /dealer/dashboard bounce.
                    path: "/dealer/users",
                    element: (
                      <Suspense fallback={null}>
                        <AdminUsersPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Dealer plan catalogue (Sprint 181, TASK-4803). Same page as
                    // /admin/plans; the catalog endpoint is dealer-aware since
                    // TASK-4801, returning the dealer's own plans plus the
                    // platform-globals (read-only, but clonable).
                    path: "/dealer/plans",
                    element: (
                      <Suspense fallback={null}>
                        <AdminPlansPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/dealer/billing",
                    element: (
                      <Suspense fallback={null}>
                        <AdminBillingPage />
                      </Suspense>
                    ),
                  },
                  {
                    path: "/dealer/activity-log",
                    element: (
                      <Suspense fallback={null}>
                        <AdminActivityLogPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Alerts inbox under the dealer shell (TASK-3676); same page, links
                    // rewritten to /dealer/* via AdminBasePathContext.
                    path: "/dealer/alerts",
                    element: (
                      <Suspense fallback={null}>
                        <AdminAlertsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Cross-tenant API keys & webhooks under the dealer shell (TASK-3725);
                    // same page as /admin/integration, dealer-scoped server-side.
                    path: "/dealer/integration",
                    element: (
                      <Suspense fallback={null}>
                        <AdminIntegrationsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Dealer self-service white-label branding (TASK-4008). Dealer-only
                    // page; writes the requesting dealer's own DealerBranding.
                    path: "/dealer/branding",
                    element: (
                      <Suspense fallback={null}>
                        <DealerBrandingPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Dealer self-service custom domains (White-Label Phase B,
                    // TASK-3905). Dealer-only; manages the requesting dealer's own
                    // DealerDomain rows.
                    path: "/dealer/domains",
                    element: (
                      <Suspense fallback={null}>
                        <DealerDomainsPage />
                      </Suspense>
                    ),
                  },
                  {
                    // Dealer self-service profile & security (Sprint 144). Renders the
                    // same ProfilePage used by the dispatcher shell so a dealer can
                    // change their password and enrol in MFA — the AppShell guard
                    // redirects dealers away from the dispatcher /profile, so this
                    // in-shell route is where the "My Profile" menu item resolves.
                    path: "/dealer/profile",
                    element: (
                      <Suspense fallback={null}>
                        <ProfilePage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // AppShell contains the ProtectedRoute guard + sidebar + header layout.
        // All authenticated dispatcher pages are nested here as children.
        element: <AppShell />,
        children: [
          {
            errorElement: <RouteErrorElement />,
            children: [
              {
                // Consolidated analytics page (Sprint 117, TASK-3828): Dashboard,
                // Planned vs Actual, Drivers, Reviews, Fleet Load, Notifications, and
                // Reports tabs in one URL-persisted shell.
                path: "/analytics",
                element: <AnalyticsPage />,
              },
              {
                // Legacy route — redirect to the Dashboard tab so existing links and
                // bookmarks keep working.
                path: "/dashboard",
                element: <Navigate to="/analytics?tab=dashboard" replace />,
              },
              {
                // TASK-4933 — «Производственный календарь», the tenant's working
                // calendar. Declared BEFORE `/routes/:id` for readability; react-router
                // 7 ranks a static segment above a dynamic one regardless of order, so
                // "calendar" can never be mistaken for a route id.
                //
                // Inside AppShell and behind its existing guard, exactly like the rest
                // of `/routes` — so a DISPATCHER reaches it, which matches the
                // backend: `TenantCalendarDayViewSet` permits `IsDispatcherOrAdmin` on
                // every action including `load-holidays`. A page a dispatcher can open
                // onto endpoints that 403 them is a feature nobody can use.
                path: "/routes/calendar",
                element: (
                  <Suspense fallback={null}>
                    <WorkingCalendarPage />
                  </Suspense>
                ),
              },
              {
                // TASK-4934 — «Что не сработало», the materialisation/release run
                // board. Static segment, declared alongside `/routes/calendar` and for
                // the same reason: react-router 7 ranks it above `/routes/:id`, so
                // "runs" can never be read as a route id.
                //
                // Inside AppShell behind the SAME guard as the rest of `/routes`, which
                // matches the backend: `RouteScheduleViewSet` (and its `runs` action)
                // permits `IsDispatcherOrAdmin`. A board a dispatcher can open onto an
                // endpoint that 403s them is a broken shell, not a feature.
                path: "/routes/runs",
                element: (
                  <Suspense fallback={null}>
                    <ScheduleRunBoardPage />
                  </Suspense>
                ),
              },
              {
                path: "/routes/:id",
                element: (
                  <Suspense fallback={null}>
                    <RouteDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "/routes",
                element: (
                  <Suspense fallback={null}>
                    {/* The section shell: the SAME path and the SAME role guard as
                before. It renders RoutesPage unchanged as the default «Рейсы»
                tab, so this is not a new route and introduces no new way to
                get authorization wrong. */}
                    <RoutesSectionPage />
                  </Suspense>
                ),
              },
              {
                path: "/orders",
                element: (
                  <Suspense fallback={null}>
                    <OrdersPage />
                  </Suspense>
                ),
              },
              {
                path: "/vehicles",
                element: (
                  <Suspense fallback={null}>
                    <VehiclesPage />
                  </Suspense>
                ),
              },
              {
                path: "/warehouses",
                element: (
                  <Suspense fallback={null}>
                    <WarehousesPage />
                  </Suspense>
                ),
              },
              {
                path: "/tracking",
                element: (
                  <Suspense fallback={null}>
                    <LiveTrackingPage />
                  </Suspense>
                ),
              },
              {
                // Legacy route — redirect to the Reports tab on the analytics page.
                path: "/reports",
                element: <Navigate to="/analytics?tab=reports" replace />,
              },
              {
                // Legacy route — redirect to the Reviews tab on the analytics page.
                path: "/feedback",
                element: <Navigate to="/analytics?tab=reviews" replace />,
              },
              {
                path: "/settings",
                element: <Navigate to="/settings/general" replace />,
              },
              {
                path: "/settings/:section",
                element: (
                  <Suspense fallback={null}>
                    <SettingsPage />
                  </Suspense>
                ),
              },
              {
                path: "/team",
                element: <Navigate to="/users" replace />,
              },
              {
                path: "/users",
                element: (
                  <Suspense fallback={null}>
                    <UsersPage />
                  </Suspense>
                ),
              },
              {
                path: "/chat",
                element: (
                  <Suspense fallback={null}>
                    <ChatPage />
                  </Suspense>
                ),
              },
              {
                path: "/billing",
                element: (
                  <Suspense fallback={null}>
                    <BillingPage />
                  </Suspense>
                ),
              },
              {
                // Integration → API Keys (company_admin self-serve). Like /billing the
                // route lives under the dispatcher shell and is surfaced only via the
                // company_admin-gated nav entry (TASK-3723).
                path: "/integration/api-keys",
                element: <Navigate to="/settings/api-keys" replace />,
              },
              {
                // Integration → Webhooks (company_admin self-serve). Like
                // /integration/api-keys the route lives under the dispatcher shell and
                // is surfaced only via the company_admin-gated nav entry (TASK-3724).
                path: "/integration/webhooks",
                element: <Navigate to="/settings/webhooks" replace />,
              },
              {
                path: "/profile",
                element: (
                  <Suspense fallback={null}>
                    <ProfilePage />
                  </Suspense>
                ),
              },
              {
                path: "*",
                element: <NotFoundPage />,
              },
            ],
          },
        ],
      },
      {
        path: "/",
        element: <Navigate to="/analytics?tab=dashboard" replace />,
      },
    ],
  },
]);

function App() {
  return (
    <>
      <Helmet>
        <title>DeliveryPlatform</title>
        <meta
          name="description"
          content="Smart delivery route optimization platform"
        />
      </Helmet>
      <Toaster position="top-right" richColors duration={4000} closeButton />
      {/* BUG-521: ONE app-level Radix tooltip provider. `TruncatedText` is used
          across the orders table, the map list panel, routes, vehicles,
          warehouses and the admin tables, and a Radix tooltip renders NOTHING
          at all without a provider above it — a silent failure, not an error.
          Hoisting it here means a new surface gets working tooltips by
          default instead of each page remembering to add its own. Existing
          page-level providers stay: nesting them is supported and each keeps
          its own delay behaviour. */}
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
      </TooltipProvider>
    </>
  );
}

export default App;
